import React, { useState, useRef, useEffect } from 'react';
import { KECService } from '../services/KECService';
import './KEC.css';

// ── Helpers ──────────────────────────────────────────────────────────────────

const ResultField = ({ value }) => (
  <span className="kec-result-field">{value || ''}</span>
);

const StepStatus = ({ status }) => (
  <span className={`kec-step-status kec-step-status--${status}`}>
    {status === 'ok' ? '✓' : status === 'error' ? '✗' : '…'}
  </span>
);

// ── Main Component ────────────────────────────────────────────────────────────

const KEC = () => {
  // Active tab — only XA is implemented in Phase 1
  const [activeTab, setActiveTab] = useState('XA');

  // ── Auth / Session state ─────────────────────────────────────────────────
  const [credType, setCredType]           = useState('public');  // 'public' | 'private'
  const [clientId, setClientId]           = useState('');
  const [clientSecret, setClientSecret]   = useState('');

  const [grantToken, setGrantToken]       = useState('');
  const [scopeToken, setScopeToken]       = useState('');
  const [resources, setResources]         = useState([]);
  const [selectedBuilding, setSelectedBuilding] = useState('');

  // ── Settings state ───────────────────────────────────────────────────────
  const [buildingType, setBuildingType] = useState('real'); // 'virtual' | 'real'
  const [buildingId, setBuildingId]     = useState('');
  const [groupId, setGroupId]           = useState('1');
  const [ken, setKen]                   = useState('');

  // ── Config / topology state ──────────────────────────────────────────────
  const [config, setConfig]             = useState(null);
  const [configResult, setConfigResult] = useState('');   // 'OK' | 'Error' | ''
  const [actionsData, setActionsData]   = useState(null);
  const [actionsResult, setActionsResult] = useState(''); // 'OK' | 'Error' | ''
  const [pingResult, setPingResult]     = useState('');

  // ── Call state ───────────────────────────────────────────────────────────
  const [sourceFloor, setSourceFloor]     = useState('');
  const [destFloor, setDestFloor]         = useState('');
  const [callType, setCallType]           = useState('dcs');  // dcs | landing | car
  const [dcsAction, setDcsAction]         = useState('');
  const [landingAction, setLandingAction] = useState('');
  const [carAction, setCarAction]         = useState('');
  const [elevatorId, setElevatorId]       = useState('');
  const [terminalId, setTerminalId]       = useState('');

  // ── Product type (Phase 2) ────────────────────────────────────────────────
  const [productType, setProductType]     = useState('person'); // 'person' | 'robot'

  // ── Robot Call / Advanced fields ──────────────────────────────────────────
  const [allocationMode, setAllocationMode] = useState('');
  const [delaySeconds, setDelaySeconds]     = useState(0);
  const [groupSize, setGroupSize]           = useState('');
  const [allowedLifts, setAllowedLifts]     = useState('');
  const [mediaType, setMediaType]           = useState('');
  const [mediaId, setMediaId]               = useState('');
  const [mediaCc, setMediaCc]               = useState('');

  // ── Step log ─────────────────────────────────────────────────────────────
  const [stepLog, setStepLog] = useState([]);

  // ── Call tracking ─────────────────────────────────────────────────────────
  const [trackEvents, setTrackEvents] = useState([]);   // [{floor, dir, moving_state, area, door, ts}]
  const [tracking, setTracking]       = useState(false);
  const trackEsRef                    = useRef(null);   // holds the EventSource
  const lastDoorRef                   = useRef(null);   // last known door state string

  // ── Loading / error state ────────────────────────────────────────────────
  const [loading, setLoading] = useState({});
  const [error, setError]     = useState('');

  // ── Hold Open state ──────────────────────────────────────────────────────
  const [holdingOpen, setHoldingOpen]     = useState(false);
  const [holdOpenDeck, setHoldOpenDeck]   = useState('');
  const [holdOpenArea, setHoldOpenArea]   = useState('');
  const [hardTime, setHardTime]           = useState(5);
  const [softTime, setSoftTime]           = useState(10);
  const holdOpenIntervalRef               = useRef(null);

  const setLoad    = (key, val) => setLoading(l => ({ ...l, [key]: val }));
  const clearError = () => setError('');

  const stopTracking = () => {
    if (trackEsRef.current) { trackEsRef.current.close(); trackEsRef.current = null; }
    setTracking(false);
  };

  // Clean up SSE on unmount
  useEffect(() => () => stopTracking(), []);

  const addLog = (step, status, detail = '') =>
    setStepLog(prev => [...prev.slice(-29), {
      id: crypto.randomUUID(), step, status, detail,
      ts: new Date().toLocaleTimeString(),
    }]);

  // ── Derive floors, lifts and terminals from topology config ─────────────────
  // Real KONE stream-v2 config shape:
  //   data.destinations[].short_name  → floor labels (e.g. "1", "1R", "2", "2R" …)
  //   data.destinations[].marking     → floor marking/name (e.g. "Lobby", "Office")
  //   data.groups[].lifts[]           → { lift_id, lift_name }
  //   data.terminals[]                → { terminal_id, name, type }
  const _extractFloors = (cfg) => {
    if (!cfg) return [];
    const d = cfg.data || cfg;
    // Primary: destinations array with short_name (real KONE QA response)
    if (Array.isArray(d.destinations) && d.destinations.length)
      return d.destinations.map(dest => dest.short_name).filter(Boolean);
    // Fallback: flat floors array
    if (Array.isArray(d.floors) && d.floors.length) return d.floors;
    if (Array.isArray(cfg.floors) && cfg.floors.length) return cfg.floors;
    // Fallback: group_floor_id from groups → lifts → floors
    const groups = d.groups || cfg.groups || [];
    if (groups.length) {
      const ids = groups.flatMap(g =>
        (g.lifts || []).flatMap(l =>
          (l.floors || []).map(f => String(f.group_floor_id))
        )
      );
      const unique = [...new Set(ids)];
      if (unique.length) return unique;
    }
    return [];
  };

  // Build a display label for each floor short_name using only confirmed
  // KONE config fields: short_name (display) + group_floor_id (internal id).
  // Format: "<short_name>  (id: <group_floor_id>)"
  const floorLabelMap = (() => {
    if (!config) return {};
    const d = config.data || config;
    if (!Array.isArray(d.destinations)) return {};
    const map = {};
    for (const dest of d.destinations) {
      if (dest.short_name == null) continue;
      const sn   = String(dest.short_name);
      const gfid = dest.group_floor_id;
      map[sn] = gfid != null ? `${sn}  (id: ${gfid})` : sn;
    }
    return map;
  })();

  // Returns the dropdown label for a floor short_name (falls back to the raw value).
  const floorLabel = (f) => floorLabelMap[String(f)] || f;

  const floors = [...new Set(_extractFloors(config))].sort((a, b) => {
    // Sort by numeric prefix first (1, 1R, 2, 2R …)
    const na = parseFloat(a), nb = parseFloat(b);
    if (!isNaN(na) && !isNaN(nb)) return na !== nb ? na - nb : String(a).localeCompare(String(b));
    return String(a).localeCompare(String(b));
  });

  // Map short_name → internal area code (area_id) for the call payload.
  // KONE lift-call-api-v2 requires numeric area IDs (e.g. 1000, 2000), not display names.
  const floorMap = (() => {
    if (!config) return {};
    const d = config.data || config;
    if (Array.isArray(d.destinations)) {
      const map = {};
      for (const dest of d.destinations) {
        if (dest.short_name != null)
          map[String(dest.short_name)] = dest.area_id ?? dest.group_floor_id ?? dest.id ?? dest.short_name;
      }
      return map;
    }
    return {};
  })();

  // Map terminal_id → [short_name, ...] — floors accessible from that terminal.
  // Used to filter source floor dropdown and prevent INVALID_SIDE errors.
  const terminalFloorMap = (() => {
    if (!config) return {};
    const d = config.data || config;
    if (!Array.isArray(d.destinations)) return {};
    const map = {};
    for (const dest of d.destinations) {
      for (const tid of (dest.terminals || [])) {
        const key = String(tid);
        if (!map[key]) map[key] = [];
        map[key].push(String(dest.short_name));
      }
    }
    return map;
  })();

  // Source floors available for the selected terminal (all floors if no terminal chosen).
  // Filters out floors on the wrong side to prevent KONE INVALID_SIDE errors.
  const sourceFloors = terminalId && terminalFloorMap[String(terminalId)]
    ? floors.filter(f => terminalFloorMap[String(terminalId)].includes(f))
    : floors;

  // Lifts: data.groups[].lifts[] → { id: lift_id, name: lift_name }
  const elevatorIds = (() => {
    if (!config) return [];
    const d = config.data || config;
    const groups = d.groups || config.groups || [];
    if (groups.length)
      return groups.flatMap(g => (g.lifts || []).map(l => ({ id: l.lift_id, name: l.lift_name || `Lift ${l.lift_id}` })));
    return config.lifts || config.data?.lifts || [];
  })();

  // Decks: groups[].lifts[].decks[] → { id: deck.area_id, liftId: lift_id, name }
  // For Car Call, area must be the deck area_id (not a floor area_id).
  const deckIds = (() => {
    if (!config) return [];
    const d = config.data || config;
    const groups = d.groups || [];
    return groups.flatMap(g =>
      (g.lifts || []).flatMap(l =>
        (l.decks || []).map(dk => ({
          id:     dk.area_id,
          liftId: l.lift_id,
          name:   `${l.lift_name || `Lift ${l.lift_id}`} (deck ${dk.deck})`,
        }))
      )
    );
  })();

  // liftDeckMap: lift_id → deck area_id (for Car Call auto-derivation from elevatorId)
  const liftDeckMap = Object.fromEntries(deckIds.map(d => [String(d.liftId), d.id]));

  // Terminals — two known KONE schemas:
  //   • Newer (5.0.15+): data.terminals[] = [{ terminal_id, name, type }]
  //   • Older (5.0.9.x): terminals are nested per group as plain IDs,
  //       data.groups[].terminals[] = [10011, 10012, ...]
  const terminalIds = (() => {
    if (!config) return [];
    const d = config.data || config;

    // Newer schema: top-level array of terminal objects
    if (Array.isArray(d.terminals) && d.terminals.length && typeof d.terminals[0] === 'object')
      return d.terminals.map(t => ({ id: t.terminal_id, name: `${t.name} (${t.type})` }));

    // Older schema: collect terminal IDs from each group (may be plain numbers)
    const groups = d.groups || config.groups || [];
    const fromGroups = groups.flatMap(g => g.terminals || []);
    if (fromGroups.length) {
      const unique = [...new Set(fromGroups.map(String))];
      return unique.map(id => ({ id, name: `Terminal ${id}` }));
    }

    // Fallback: top-level array of plain IDs
    if (Array.isArray(d.terminals) && d.terminals.length)
      return d.terminals.map(id => ({ id: String(id), name: `Terminal ${id}` }));

    return config.terminals || [];
  })();

  // ---------------------------------------------------------------------------
  // _deriveCallActions — parse the /api/kec/actions WS response.
  // Real KONE response shape:  data.call_types[]  { action_type, action_id, name, enabled }
  //   action_type: "DestinationCall" → dcsCall
  //                "LandingCall"     → landingCall
  //                "CarCall"         → carCall
  //   data.functions[]               → functionCall
  // Returns: { dcsCall: [{id, name}], landingCall: [...], carCall: [...], functionCall: [...] }
  // ---------------------------------------------------------------------------
  const _deriveCallActions = (actResp) => {
    if (!actResp) return {};
    const d = actResp.data || actResp;
    const result = { dcsCall: [], landingCall: [], carCall: [], functionCall: [] };

    // Primary: KONE stream-v2 actions response — data.call_types[]
    if (Array.isArray(d.call_types) && d.call_types.length) {
      const typeMap = { DestinationCall: 'dcsCall', LandingCall: 'landingCall', CarCall: 'carCall' };
      for (const ct of d.call_types) {
        if (!ct.enabled) continue;
        const key = typeMap[ct.action_type];
        if (key) result[key].push({ id: ct.action_id, name: ct.name });
      }
    }
    // Functions: data.functions[]
    if (Array.isArray(d.functions) && d.functions.length) {
      for (const fn of d.functions) {
        if (fn.enabled) result.functionCall.push({ id: fn.action_id, name: fn.name });
      }
    }
    return result;
  };

  // actionsForType(callType) — returns [{id, name}] for a given call type
  const actionsForType = (callType) => {
    if (!actionsData) return [];
    const derived = _deriveCallActions(actionsData);
    return derived[callType] || [];
  };

  const effectiveBuildingId = buildingType === 'virtual' ? selectedBuilding : buildingId;

  // Region derived from active tab: OA (CN tab) → 'cn', XA (GL tab) → 'global'
  const region = activeTab === 'OA' ? 'cn' : 'global';

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleKenLookup = async () => {
    clearError();
    setLoad('kenLookup', true);
    try {
      // Auto-fetch grant token if not available
      let token = grantToken;
      if (!token) {
        const tokenRes = await KECService.getGrantToken(clientId, clientSecret, credType, '', region);
        if (tokenRes.success) {
          token = tokenRes.grantToken;
          setGrantToken(token);
          addLog('Get Token', 'ok', `token: ${token.slice(0, 12)}…`);
        } else {
          setError(tokenRes.error || 'Failed to get grant token for KEN lookup');
          addLog('KEN Lookup', 'error', 'Could not get grant token');
          return;
        }
      }
      const res = await KECService.deviceMapping(ken, token, region);
      if (res.success) {
        setBuildingId(res.buildingId);
        setGroupId(res.groupId || '1');
        addLog('KEN Lookup', 'ok', `KEN ${ken} → building: ${res.buildingId}, group: ${res.groupId}`);
      } else {
        setError(res.error || 'KEN lookup failed');
        addLog('KEN Lookup', 'error', res.error || 'Failed');
      }
    } catch (e) {
      setError(e.message);
      addLog('KEN Lookup', 'error', e.message);
    } finally {
      setLoad('kenLookup', false);
    }
  };

  const handleGetGrantToken = async () => {
    clearError();
    setLoad('grant', true);
    try {
      const res = await KECService.getGrantToken(clientId, clientSecret, credType, '', region);
      if (res.success) {
        setGrantToken(res.grantToken);
        addLog('Get Token', 'ok', `token: ${res.grantToken.slice(0, 12)}…`);
      } else {
        setError(res.error || 'Failed to get grant token');
        addLog('Get Token', 'error', res.error || 'Failed');
      }
    } catch (e) {
      setError(e.message);
      addLog('Get Token', 'error', e.message);
    } finally {
      setLoad('grant', false);
    }
  };

  const handleGetResources = async () => {
    clearError();
    setLoad('resources', true);
    try {
      const res = await KECService.getResources(grantToken, region);
      if (res.success) {
        setResources(res.resources || []);
        addLog('Get Resources', 'ok', `${(res.resources || []).length} building(s)`);
      } else {
        setError(res.error || 'Failed to get resources');
        addLog('Get Resources', 'error', res.error || 'Failed');
      }
    } catch (e) {
      setError(e.message);
      addLog('Get Resources', 'error', e.message);
    } finally {
      setLoad('resources', false);
    }
  };

  const handleGetScopeToken = async () => {
    clearError();
    setLoad('scope', true);
    try {
      const res = await KECService.getScopeToken(clientId, clientSecret, effectiveBuildingId, groupId, credType, '', region);
      if (res.success) {
        setScopeToken(res.scopeToken);
        addLog('Get Scope Token', 'ok', `token: ${res.scopeToken.slice(0, 12)}…`);
      } else {
        setError(res.error || 'Failed to get scope token');
        addLog('Get Scope Token', 'error', res.error || 'Failed');
      }
    } catch (e) {
      setError(e.message);
      addLog('Get Scope Token', 'error', e.message);
    } finally {
      setLoad('scope', false);
    }
  };

  const handleGetConfig = async () => {
    clearError();
    setLoad('config', true);
    try {
      const res = await KECService.getConfig(scopeToken, effectiveBuildingId, groupId, grantToken, ken, region);
      if (res.success) {
        console.log('[KEC] config response:', JSON.stringify(res.config, null, 2));
        setConfig(res.config);
        setConfigResult('OK');
        const d = res.config?.data || res.config || {};
        const floorCount    = _extractFloors(res.config).length;
        const terminalCount = (d.terminals || []).length;
        const liftNames     = (d.groups || []).flatMap(g => (g.lifts || []).map(l => `${l.lift_name || 'Lift'} (${l.lift_id})`)).join(', ');
        const detail = [
          `${floorCount} floor(s)`,
          terminalCount ? `${terminalCount} terminal(s)` : null,
          liftNames     ? `lifts: ${liftNames}` : null,
        ].filter(Boolean).join(' · ');
        addLog('Get Config', 'ok', detail);
      } else {
        setConfigResult('Error');
        setError(res.error || 'Failed to get config');
        addLog('Get Config', 'error', res.error || 'Failed');
      }
    } catch (e) {
      setConfigResult('Error');
      setError(e.message);
      addLog('Get Config', 'error', e.message);
    } finally {
      setLoad('config', false);
    }
  };

  const handleGetAction = async () => {
    clearError();
    setLoad('action', true);
    try {
      if (!scopeToken || !effectiveBuildingId) {
        setError('Scope token and building ID are required');
        addLog('Get Actions', 'error', 'missing scope token or building ID');
        setActionsResult('Error');
        return;
      }
      const res = await KECService.getActions(scopeToken, effectiveBuildingId, groupId, region);
      if (res.success) {
        setActionsData(res.actions);
        setActionsResult('OK');
        const derived = _deriveCallActions(res.actions);
        const parts = [
          derived.dcsCall?.length     ? `DCS: ${derived.dcsCall.length}`     : null,
          derived.landingCall?.length ? `Landing: ${derived.landingCall.length}` : null,
          derived.carCall?.length     ? `Car: ${derived.carCall.length}`     : null,
          derived.functionCall?.length? `Fn: ${derived.functionCall.length}` : null,
        ].filter(Boolean);
        const total = Object.values(derived).reduce((s, a) => s + a.length, 0);
        addLog('Get Actions', 'ok', `${total} action(s) · ${parts.join(', ')}`);
      } else {
        setActionsResult('Error');
        setError(res.error || 'Failed to get actions');
        addLog('Get Actions', 'error', res.error || 'Failed');
      }
    } catch (e) {
      setActionsResult('Error');
      setError(e.message);
      addLog('Get Actions', 'error', e.message);
    } finally {
      setLoad('action', false);
    }
  };

  const handlePing = async () => {
    clearError();
    setLoad('ping', true);
    try {
      const res = await KECService.pingElevator(grantToken, effectiveBuildingId, scopeToken, groupId || '1', region);
      if (res.success) {
        // Show the device timestamp from the ping response alongside the status,
        // so users can verify the KCEGCH device is running with the correct time.
        const t = res.deviceTime ? ` @ ${res.deviceTime}` : '';
        setPingResult(`Reachable${t}`);
        addLog('Ping', 'ok', res.deviceTime
          ? `elevator reachable · device time: ${res.deviceTime}`
          : 'elevator reachable');
      } else {
        setPingResult('Unreachable');
        setError(res.error || 'Ping failed');
        addLog('Ping', 'error', res.error || 'Failed');
      }
    } catch (e) {
      setError(e.message);
      setPingResult('Error');
      addLog('Ping', 'error', e.message);
    } finally {
      setLoad('ping', false);
    }
  };

  const handleCallElevator = async () => {
    clearError();
    setLoad('call', true);
    try {
      // Car call: deck area_id auto-derived from selected elevator (falls back to first deck)
      // DCS/Landing use floor area_id from floorMap
      const srcArea = callType === 'car'
        ? (liftDeckMap[String(elevatorId)] ?? deckIds[0]?.id ?? 0)
        : (floorMap[String(sourceFloor)] ?? sourceFloor);
      const dstArea = floorMap[String(destFloor)]   ?? destFloor;
      const result  = await KECService.callElevator(
        scopeToken, effectiveBuildingId, groupId || '1', callType,
        srcArea, dstArea,
        dcsAction, landingAction, carAction, '',
        elevatorId, terminalId, ken,
        {
          productType,
          allocationMode,
          delay: delaySeconds,
          groupSize,
          allowedLifts: allowedLifts ? allowedLifts.split(',').map(l => l.trim()) : [],
          mediaType,
          mediaId,
          mediaCc,
          region,
        }
      );
      const messages = result.messages || [];
      const ackMsg   = messages[0] || {};
      const dataMsg  = messages[1] || {};
      const callData = dataMsg.data || {};

      // 201 ACK
      const ackOk = ackMsg.statusCode === 201;
      addLog('Call 201 ACK', ackOk ? 'ok' : 'error',
        ackOk ? `connId: ${(ackMsg.connectionId || '').slice(0, 12)}` : `status: ${ackMsg.statusCode}`);

      // Session ID
      const sessionId = callData.session_id;
      addLog('Call Session ID', sessionId != null ? 'ok' : 'error',
        sessionId != null ? `session: ${sessionId}` : (callData.error || 'no session'));

      // Call tracking
      const tracking = callData.allocation_mode;
      addLog('Call Tracking', tracking ? 'ok' : 'error',
        tracking || callData.error || 'no tracking info');

      if (callData.success === false) setError(callData.error || 'Call rejected by KONE');

      // ── Auto-populate Hold Open fields from call result ────────────────
      // Lead suggestion: auto-select lift deck + served area based on allocated lift + source/destination
      const allocatedLiftId = elevatorId || (deckIds[0]?.liftId ?? '');
      const allocatedDeck = liftDeckMap[String(allocatedLiftId)] || (deckIds[0]?.id ?? '');
      const sourceArea = floorMap[String(sourceFloor)] ?? sourceFloor ?? '';
      if (allocatedDeck) setHoldOpenDeck(String(allocatedDeck));
      if (sourceArea) setHoldOpenArea(String(sourceArea));

      // ── Start lift-deck-position tracking if call succeeded ──────────────
      if (ackOk && sessionId != null) {
        stopTracking();
        setTrackEvents([]);
        lastDoorRef.current = null;
        setTracking(true);
        const liftIdForTrack = elevatorId || (deckIds[0]?.liftId ?? '1');
        const es = KECService.trackLift(
          scopeToken, effectiveBuildingId, groupId || '1', liftIdForTrack,
          (type, msg) => {
            if (type === 'position') {
              const d = msg.data || {};
              // d.door: truthy = door active, falsy = door inactive
              const doorActive = !!d.door;
              const doorStr = doorActive
                ? (lastDoorRef.current || (typeof d.door === 'string' ? d.door : 'open'))
                : null;
              if (!doorActive) lastDoorRef.current = null; // reset when door goes inactive
              setTrackEvents(prev => [...prev.slice(-49), {
                floor:        d.cur,
                advFloor:     d.adv,
                dir:          d.dir,
                moving_state: d.moving_state,
                area:         d.area,
                door:         doorStr,
                serving_mode: d.serving_mode,
                ts:           new Date().toLocaleTimeString(),
              }]);
            } else if (type === 'door_state') {
              const d = msg.data || {};
              // try every possible KONE field structure for door state
              const rawDoor =
                d.state ||
                d.door_state ||
                d.status ||
                d.decks?.[0]?.state ||
                d.decks?.[0]?.doors?.[0]?.state ||
                d.doors?.[0]?.state ||
                null;
              const doorStr = !rawDoor ? null
                : typeof rawDoor === 'string' ? rawDoor
                : rawDoor === true ? 'open'
                : (rawDoor?.state || null);
              if (doorStr) lastDoorRef.current = doorStr;

              // ── Auto-update Hold Open served_area when doors OPEN at a landing ──
              const doorState = (typeof rawDoor === 'string') ? rawDoor.toUpperCase() : '';
              if ((doorState === 'OPENED' || doorState === 'OPENING') && d.landing) {
                setHoldOpenArea(String(d.landing));
              }

              // update door field on the most recent position row
              setTrackEvents(prev => {
                if (prev.length === 0) return prev;
                const updated = [...prev];
                const last = { ...updated[updated.length - 1], door: doorStr || updated[updated.length - 1].door };
                updated[updated.length - 1] = last;
                return updated;
              });
            } else if (type === 'call_state') {
              const d = msg.data || {};
              setTrackEvents(prev => [...prev.slice(-49), {
                floor: null,
                dir: null,
                moving_state: `call: ${d.state || msg.subtopic || 'state'}`,
                ts: new Date().toLocaleTimeString(),
              }]);
            } else if (type === 'done' || type === 'error' || type === 'timeout') {
              setTracking(false);
              trackEsRef.current = null;
            }
          },
          60,
          sessionId
        );
        trackEsRef.current = es;
      }
    } catch (e) {
      setError(e.message);
      addLog('Call', 'error', e.message);
    } finally {
      setLoad('call', false);
    }
  };

  // DCS call:     source + destination
  // Landing call: source only
  // Car call:     destination only (deck area derived from elevator ID)
  const showSource = callType === 'dcs' || callType === 'landing';
  const showDest   = callType === 'dcs' || callType === 'car';
  const callDisabled = loading.call || !scopeToken
    || (showSource && !sourceFloor)
    || (showDest && !destFloor);

  // ── Hold Open handlers ──────────────────────────────────────────────────
  const handleHoldOpen = async () => {
    clearError();
    try {
      const deck = holdOpenDeck || (deckIds[0]?.id ?? '');
      const area = holdOpenArea || (floorMap[sourceFloor] ?? sourceFloor ?? '');
      if (!deck || !area) {
        setError('Lift deck and served area are required for Hold Open');
        addLog('Hold Open', 'error', 'missing deck or area');
        return;
      }
      const res = await KECService.holdOpen(scopeToken, effectiveBuildingId, groupId, deck, area, hardTime, softTime, region);
      if (res.success) {
        addLog('Hold Open', 'ok', `deck: ${deck}, area: ${area}, hard: ${hardTime}s`);
      } else {
        setError(res.error || 'Hold open failed');
        addLog('Hold Open', 'error', res.error || 'Failed');
      }
    } catch (e) {
      setError(e.message);
      addLog('Hold Open', 'error', e.message);
    }
  };

  const startHoldOpen = () => {
    if (holdingOpen) return;
    setHoldingOpen(true);
    handleHoldOpen(); // immediate first call
    holdOpenIntervalRef.current = setInterval(handleHoldOpen, 5000); // repeat every 5s
  };

  const stopHoldOpen = () => {
    setHoldingOpen(false);
    if (holdOpenIntervalRef.current) {
      clearInterval(holdOpenIntervalRef.current);
      holdOpenIntervalRef.current = null;
    }
    addLog('Hold Open', 'ok', 'stopped');
  };

  // Clean up hold open interval on unmount
  useEffect(() => () => { if (holdOpenIntervalRef.current) clearInterval(holdOpenIntervalRef.current); }, []);

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <div className="kec-root">

      {/* ── Tab bar ── */}
      <div className="kec-tabs">
        {[
          { id: 'XA', label: 'XA Manager GL' },
          { id: 'OA', label: 'XA Manager CN' },
          { id: 'AA', label: 'AA Manager AA' },
          { id: 'BlackSmith', label: 'BlackSmith Manager' },
        ].map(tab => (
          <button
            key={tab.id}
            className={`kec-tab${activeTab === tab.id ? ' kec-tab--active' : ''}${(tab.id !== 'XA' && tab.id !== 'OA') ? ' kec-tab--disabled' : ''}`}
            onClick={() => (tab.id === 'XA' || tab.id === 'OA') && setActiveTab(tab.id)}
            title={(tab.id !== 'XA' && tab.id !== 'OA') ? 'Coming soon' : ''}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ── Body ── */}
      <div className="kec-body">

        {/* ──────────────── LEFT COLUMN ──────────────── */}
        <div className="kec-left">

          {/* PRODUCT */}
          <fieldset className="kec-fieldset">
            <legend>PRODUCT</legend>
            <label className="kec-radio">
              <input type="radio" name="product" checked={productType === 'person'}
                onChange={() => setProductType('person')} />
              Elevator Call
            </label>
            <label className="kec-radio">
              <input type="radio" name="product" checked={productType === 'robot'}
                onChange={() => setProductType('robot')} />
              Robot Call
            </label>
          </fieldset>

          {/* AUTHENTICATION */}
          <fieldset className="kec-fieldset">
            <legend>AUTHENTICATION</legend>

            {/* ── Credential type ── */}
            <label className="kec-radio">
              <input type="radio" name="credType"
                checked={credType === 'public'}
                onChange={() => { setCredType('public'); setBuildingId(''); setGroupId('1'); setSelectedBuilding(''); }} />
              Public Credentials
            </label>
            <label className="kec-radio" style={{ marginTop: 4 }}>
              <input type="radio" name="credType"
                checked={credType === 'private'}
                onChange={() => { setCredType('private'); setBuildingId(''); setGroupId('1'); setSelectedBuilding(''); }} />
              Private Credentials
            </label>
            {credType === 'private' && (
              <div className="kec-cred-sub">
                <div className="kec-form-row">
                  <label>Client ID</label>
                  <input className="kec-input" value={clientId}
                    onChange={e => setClientId(e.target.value)} placeholder="Client ID" />
                </div>
                <div className="kec-form-row">
                  <label>Client Secret</label>
                  <input className="kec-input" type="password" value={clientSecret}
                    onChange={e => setClientSecret(e.target.value)} placeholder="Client Secret" />
                </div>
              </div>
            )}

            {/* ── Building type (shown after credential type is chosen) ── */}
            <div className="kec-section-divider" />
            <label className="kec-radio">
              <input
                type="radio"
                name="buildingType"
                value="virtual"
                checked={buildingType === 'virtual'}
                onChange={() => { setBuildingType('virtual'); setBuildingId(''); setGroupId('1'); setSelectedBuilding(''); }}
              /> Virtual Building
            </label>
            <label className="kec-radio" style={{ marginTop: 4 }}>
              <input
                type="radio"
                name="buildingType"
                value="real"
                checked={buildingType === 'real'}
                onChange={() => { setBuildingType('real'); setSelectedBuilding(''); }}
              /> Real Building
            </label>

            {/* Real → manual Building ID + Group ID */}
            {buildingType === 'real' && (
              <div className="kec-cred-sub">
                <div className="kec-form-row">
                  <label>Building ID</label>
                  <input
                    className="kec-input"
                    value={buildingId}
                    onChange={e => setBuildingId(e.target.value)}
                    placeholder="Building ID"
                  />
                </div>
                <div className="kec-form-row">
                  <label>Group ID</label>
                  <input
                    className="kec-input kec-input--xs"
                    value={groupId}
                    onChange={e => setGroupId(e.target.value)}
                    placeholder="Group ID"
                  />
                </div>
                <div className="kec-form-row">
                  <label>KEN</label>
                  <input
                    className="kec-input kec-input--sm"
                    value={ken}
                    onChange={e => setKen(e.target.value)}
                    placeholder="e.g. 42455100"
                  />
                  <button className="kec-btn" onClick={handleKenLookup}
                    disabled={!ken || loading.kenLookup}>
                    {loading.kenLookup ? '…' : 'Lookup'}
                  </button>
                </div>
              </div>
            )}

            <div className="kec-section-divider" />

            <div className="kec-btn-row">
              <button className="kec-btn" onClick={handleGetGrantToken}
                disabled={
                  loading.grant
                  || (credType === 'private' && (!clientId || !clientSecret))
                }>
                {loading.grant ? '…' : 'Get Grant Token'}
              </button>
              <ResultField value={grantToken ? `✓ ${grantToken.slice(0, 12)}…` : ''} />
            </div>

            {buildingType === 'virtual' && (
              <div className="kec-btn-row">
                <button
                  className="kec-btn"
                  onClick={handleGetResources}
                  disabled={loading.resources || !grantToken}
                >
                  {loading.resources ? '...' : 'Get Resources'}
                </button>
                <select
                  className="kec-select kec-select--sm"
                  value={selectedBuilding}
                  onChange={e => {
                    const val = e.target.value;
                    setSelectedBuilding(val);
                    if (val) setGroupId('1');
                  }}
                >
                  <option value="">-- Select Building --</option>
                  {resources.map((r, i) => {
                    const id   = r.buildingId || r.id || r;
                    const name = r.name || r.buildingName || id;
                    return <option key={i} value={id}>{name}</option>;
                  })}
                </select>
              </div>
            )}

            <div className="kec-btn-row">
              <button
                className="kec-btn"
                onClick={handleGetScopeToken}
                disabled={loading.scope || !grantToken || !effectiveBuildingId}
              >
                {loading.scope ? '...' : 'Get Scope Token'}
              </button>
              <ResultField value={scopeToken ? `✓ ${scopeToken.slice(0, 12)}…` : ''} />
            </div>
            {effectiveBuildingId && (
              <div className="kec-building-id-display">
                Building: <span>{effectiveBuildingId}</span>
              </div>
            )}
          </fieldset>

          {/* INITIALIZATION */}
          <fieldset className="kec-fieldset">
            <legend>INITIALIZATION</legend>

            <div className="kec-btn-row">
              <button className="kec-btn" onClick={handleGetConfig}
                disabled={loading.config || !scopeToken}>
                {loading.config ? '…' : 'Get Config'}
              </button>
              <ResultField value={configResult} />
            </div>

            <div className="kec-btn-row">
              <button className="kec-btn" onClick={handleGetAction}
                disabled={loading.action || !config}>
                {loading.action ? '…' : 'Get Actions'}
              </button>
              <ResultField value={actionsResult} />
            </div>

            <div className="kec-btn-row">
              <button className="kec-btn" onClick={handlePing}
                disabled={loading.ping || !scopeToken}>
                {loading.ping ? '…' : 'Ping Elevator'}
              </button>
              <ResultField value={pingResult} />
            </div>
          </fieldset>
        </div>

        {/* ──────────────── RIGHT COLUMN ──────────────── */}
        <div className="kec-right">

          {/* BASIC CALL */}
          <fieldset className="kec-fieldset kec-fieldset--call">
            <legend>BASIC CALL</legend>

            {/* Call type selection */}
            <div className="kec-call-type-row">
              <span className="kec-label-inline">Call Type:</span>
              <label className="kec-radio-inline">
                <input type="radio" name="callType" value="dcs"
                  checked={callType === 'dcs'}
                  onChange={() => { setCallType('dcs'); setSourceFloor(''); setDestFloor(''); }} />
                DCS Call <span className="kec-hint">(source + destination)</span>
              </label>
              <label className="kec-radio-inline">
                <input type="radio" name="callType" value="landing"
                  checked={callType === 'landing'}
                  onChange={() => { setCallType('landing'); setDestFloor(''); }} />
                Landing Call <span className="kec-hint">(source only)</span>
              </label>
              <label className="kec-radio-inline">
                <input type="radio" name="callType" value="car"
                  checked={callType === 'car'}
                  onChange={() => { setCallType('car'); setSourceFloor(''); }} />
                Car Call <span className="kec-hint">(destination only)</span>
              </label>
            </div>

            {/* Floor + action fields — conditional on call type */}
            <div className="kec-call-fields">

              {showSource && (
                <div className="kec-call-field-group">
                  <div className="kec-form-row">
                    <label className="kec-form-label">Source Floor</label>
                    <select className="kec-select" value={sourceFloor} onChange={e => setSourceFloor(e.target.value)}>
                      <option value="">-- Source Floor --</option>
                      {sourceFloors.map(f => <option key={f} value={f}>{floorLabel(f)}</option>)}
                    </select>
                  </div>
                  {callType === 'dcs' && (
                    <div className="kec-form-row">
                      <label className="kec-form-label">DCS Action</label>
                      <select className="kec-select" value={dcsAction} onChange={e => setDcsAction(e.target.value)}>
                        <option value="">-- Action --</option>
                        {actionsForType('dcsCall').map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    </div>
                  )}
                  {callType === 'landing' && (
                    <div className="kec-form-row">
                      <label className="kec-form-label">Landing Action</label>
                      <select className="kec-select" value={landingAction} onChange={e => setLandingAction(e.target.value)}>
                        <option value="">-- Action --</option>
                        {actionsForType('landingCall').map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {showDest && (
                <div className="kec-call-field-group">
                  <div className="kec-form-row">
                    <label className="kec-form-label">Destination Floor</label>
                    <select className="kec-select" value={destFloor} onChange={e => setDestFloor(e.target.value)}>
                      <option value="">-- Destination Floor --</option>
                      {floors.map(f => <option key={f} value={f}>{floorLabel(f)}</option>)}
                    </select>
                  </div>
                  {callType === 'car' && (
                    <div className="kec-form-row">
                      <label className="kec-form-label">Car Action</label>
                      <select className="kec-select" value={carAction} onChange={e => setCarAction(e.target.value)}>
                        <option value="">-- Action --</option>
                        {actionsForType('carCall').map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                      </select>
                    </div>
                  )}
                </div>
              )}

              {/* Terminal and Elevator IDs */}
              <div className="kec-call-field-group">
                <div className="kec-form-row">
                  <label className="kec-form-label">Terminal ID</label>
                  <select className="kec-select" value={terminalId} onChange={e => { setTerminalId(e.target.value); }}>
                    <option value="">-- Terminal ID --</option>
                    {terminalIds.map((t, i) => <option key={i} value={t?.id || t}>{t?.name || t?.id || t}</option>)}
                  </select>
                </div>
                <div className="kec-form-row">
                  <label className="kec-form-label">Elevator ID</label>
                  <select className="kec-select" value={elevatorId} onChange={e => setElevatorId(e.target.value)}>
                    <option value="">-- Elevator ID --</option>
                    {elevatorIds.map((e, i) => <option key={i} value={e?.id || e}>{(e?.name || e?.id || e) + (e?.id ? ` (${e.id})` : '')}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Call button */}
            <div className="kec-call-action">
              <button className="kec-btn kec-btn--primary"
                onClick={handleCallElevator} disabled={callDisabled}>
                {loading.call ? 'Calling…' : 'Call Elevator'}
              </button>
            </div>

            {/* ADVANCE CALL OPTIONS */}
            <fieldset className="kec-fieldset kec-fieldset--advance">
              <legend>ADVANCE CALL OPTIONS</legend>
              <div className="kec-advance-buttons">
                <button
                  className={`kec-btn kec-btn--toggle ${mediaType ? 'kec-btn--active' : ''}`}
                  onClick={() => setMediaType(mediaType ? '' : 'RFID')}>
                  Access Control Call
                </button>
                <button
                  className={`kec-btn kec-btn--toggle ${delaySeconds > 0 ? 'kec-btn--active' : ''}`}
                  onClick={() => setDelaySeconds(delaySeconds ? 0 : 10)}>
                  Delay Call
                </button>
              </div>

              {/* Access Control Fields */}
              {mediaType && (
                <div className="kec-advance-section">
                  <div className="kec-form-row">
                    <label className="kec-form-label">Media Type</label>
                    <select className="kec-select" value={mediaType} onChange={e => setMediaType(e.target.value)}>
                      <option value="RFID">RFID</option>
                      <option value="BLUETOOTH">BLUETOOTH</option>
                      <option value="FACE">FACE</option>
                      <option value="QR">QR</option>
                    </select>
                  </div>
                  <div className="kec-form-row">
                    <label className="kec-form-label">Media CC</label>
                    <input className="kec-input" value={mediaCc}
                      onChange={e => setMediaCc(e.target.value)} placeholder="e.g. abc_0123" />
                  </div>
                  <div className="kec-form-row">
                    <label className="kec-form-label">Media ID</label>
                    <input className="kec-input" value={mediaId}
                      onChange={e => setMediaId(e.target.value)} placeholder="e.g. 0123345abcd" />
                  </div>
                </div>
              )}

              {/* Delay Call Fields */}
              {delaySeconds > 0 && (
                <div className="kec-advance-section">
                  <div className="kec-form-row">
                    <label className="kec-form-label">Walking Time (sec)</label>
                    <input className="kec-input kec-input--xs" type="number" min="0" max="30"
                      value={delaySeconds} onChange={e => setDelaySeconds(Number(e.target.value))} />
                  </div>
                </div>
              )}

              {/* Group Size */}
              {productType === 'robot' && (
                <div className="kec-form-row">
                  <label className="kec-form-label">Group Size</label>
                  <input className="kec-input kec-input--xs" type="text" 
                    value={groupSize} onChange={e => setGroupSize(e.target.value.replace(/[^0-9]/g, ''))} />
                </div>
              )}

              {/* Allowed Lifts */}
              {productType === 'robot' && (
                <div className="kec-form-row">
                  <label className="kec-form-label">Allowed Lifts</label>
                  <input className="kec-input" value={allowedLifts}
                    onChange={e => setAllowedLifts(e.target.value)}
                    placeholder={deckIds.length ? deckIds.map(d => d.id).join(', ') : 'e.g. 1001010, 1001110'} />
                </div>
              )}

              {/* HOLD DOOR OPEN */}
              <div className="kec-advance-section" style={{ marginTop: '12px' }}>
                <strong style={{ fontSize: '11px', color: '#aaa', textTransform: 'uppercase' }}>Hold Door Open</strong>
                <div className="kec-form-row">
                  <label className="kec-form-label">Lift Deck</label>
                  <select className="kec-select" value={holdOpenDeck} onChange={e => setHoldOpenDeck(e.target.value)}>
                    <option value="">-- Select Deck --</option>
                    {deckIds.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                  </select>
                </div>
                <div className="kec-form-row">
                  <label className="kec-form-label">Served Area</label>
                  <select className="kec-select" value={holdOpenArea} onChange={e => setHoldOpenArea(e.target.value)}>
                    <option value="">-- Select Floor --</option>
                    {floors.map(f => <option key={f} value={floorMap[f] || f}>{f}</option>)}
                  </select>
                </div>
                <div className="kec-form-row">
                  <label className="kec-form-label">Hard Time (sec)</label>
                  <input className="kec-input kec-input--xs" type="number" min="0" max="10"
                    value={hardTime} onChange={e => setHardTime(Number(e.target.value))} />
                </div>
                <div className="kec-form-row">
                  <label className="kec-form-label">Soft Time (sec)</label>
                  <input className="kec-input kec-input--xs" type="number" min="0" max="30"
                    value={softTime} onChange={e => setSoftTime(Number(e.target.value))} />
                </div>
                <div className="kec-btn-row">
                  {!holdingOpen ? (
                    <button className="kec-btn" onClick={startHoldOpen}
                      disabled={!scopeToken || !effectiveBuildingId}>
                      Hold Open
                    </button>
                  ) : (
                    <button className="kec-btn kec-btn--danger" onClick={stopHoldOpen}>
                      Stop Holding
                    </button>
                  )}
                  {holdingOpen && <span className="kec-track-live"> ● HOLDING</span>}
                </div>
              </div>
            </fieldset>
          </fieldset>

          {/* CALL TRACKING */}
          <fieldset className="kec-fieldset kec-fieldset--track">
            <legend>
              CALL TRACKING
              {tracking && <span className="kec-track-live"> ● LIVE</span>}
              {!tracking && trackEvents.length > 0 && (
                <button className="kec-log-clear" onClick={() => setTrackEvents([])}>clear</button>
              )}
              {tracking && (
                <button className="kec-log-clear" onClick={stopTracking}>stop</button>
              )}
            </legend>
            {trackEvents.length === 0 && !tracking
              ? <div className="kec-log-empty">No tracking data yet. Make a call to start.</div>
              : (
                <div className="kec-track-list">
                  {trackEvents.map((e, i) => (
                    e.floor === null
                      ? (
                        <div key={i} className="kec-track-entry kec-track-entry--state">
                          <span className="kec-track-state" style={{gridColumn:'1 / 4'}}>{e.moving_state}</span>
                          <span className="kec-track-ts">{e.ts}</span>
                        </div>
                      ) : (
                        <div key={i} className="kec-track-entry">
                          <span className="kec-track-floor">Floor {e.floor ?? '—'}</span>
                          <span className={`kec-track-dir kec-track-dir--${(e.dir || '').toLowerCase()}`}>{e.dir || '—'}</span>
                          <span className="kec-track-state">{e.moving_state || '—'}</span>
                          {e.door && (
                            <span className={`kec-track-door kec-track-door--${e.door.toLowerCase().replace(/\s+/g, '_')}`}>
                              {e.door.toUpperCase()}
                            </span>
                          )}
                          <span className="kec-track-ts">{e.ts}</span>
                        </div>
                      )
                  ))}
                </div>
              )
            }
          </fieldset>

          {/* STEP LOG */}
          <fieldset className="kec-fieldset kec-fieldset--log">
            <legend>
              STEP LOG
              {stepLog.length > 0 && (
                <button className="kec-log-clear" onClick={() => setStepLog([])}>clear</button>
              )}
            </legend>
            {stepLog.length === 0
              ? <div className="kec-log-empty">No steps run yet.</div>
              : (
                <div className="kec-log-list">
                  {stepLog.map(entry => (
                    <div key={entry.id} className={`kec-log-entry kec-log-entry--${entry.status}`}>
                      <StepStatus status={entry.status} />
                      <span className="kec-log-step">{entry.step}</span>
                      <span className="kec-log-detail">{entry.detail}</span>
                      <span className="kec-log-ts">{entry.ts}</span>
                    </div>
                  ))}
                </div>
              )
            }
          </fieldset>

        </div>
      </div>

      {/* Error bar */}
      {error && (
        <div className="kec-error">
          <strong>Error:</strong> {error}
          <button className="kec-error-close" onClick={clearError}>✕</button>
        </div>
      )}
    </div>
  );
};

export default KEC;
